/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Localizer from '../../helpers/localization';
import AccessibleButton from '../buttons/AccessibleButton';
import './Equalizer.css';
import Tooltip from '../tooltip/Tooltip';

/* eslint-disable no-magic-numbers */
const EQUALIZER = Object.freeze({
  AnimationSpeed: 180,
  BarCount: 5,
  MinValues: Object.freeze([0, 0.3, 0.4, 0.5, 0.4, 0.2]),
  RangeValues: Object.freeze([0, 0.5, 0.6, 0.4, 0.5, 0.6]),
});
/* eslint-enable no-magic-numbers */

const getEqualizerBarHeights = (): Array<number> => {
  const barHeights: Array<number> = [];

  for (let i = 1; i < EQUALIZER.BarCount + 1; i++) {
    barHeights.push(EQUALIZER.MinValues[i] + Math.random() * EQUALIZER.RangeValues[i]);
  }

  return barHeights;
};

type EqualizerPropType = {
  +isMuted: boolean,
  +onClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => void,
};

const Equalizer = ({ isMuted, onClick }: EqualizerPropType): React.Node => {
  const equalizerAnimationTimer = useRef<IntervalID | null>(null);

  const [barHeights, setBarHeights] = useState<Array<number>>(() => getEqualizerBarHeights());

  const updateEqualizer = useCallback(() => {
    setBarHeights(getEqualizerBarHeights());
  }, []);

  const startEqualizerAnimation = useCallback(() => {
    if (equalizerAnimationTimer.current !== null) {
      // Already started
      return;
    }

    equalizerAnimationTimer.current = setInterval(updateEqualizer, EQUALIZER.AnimationSpeed);
  }, [updateEqualizer]);

  const stopEqualizerAnimation = useCallback(() => {
    if (equalizerAnimationTimer.current !== null) {
      clearInterval(equalizerAnimationTimer.current);
      equalizerAnimationTimer.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      stopEqualizerAnimation();
    },
    [stopEqualizerAnimation],
  );

  useEffect(() => {
    if (isMuted) {
      stopEqualizerAnimation();
    } else {
      startEqualizerAnimation();
    }
  }, [isMuted, startEqualizerAnimation, stopEqualizerAnimation]);

  // eslint-disable-next-line react/no-array-index-key
  const bars = barHeights.map((h, i) => <span key={i} style={{ transform: `scaleY(${h})` }} />);

  return (
    <Tooltip content={<div className='tooltipContent button'>{Localizer.localize('carousel.tooltip.toggle_sound')}</div>}>
      <AccessibleButton aria-label={Localizer.localize('carousel.tooltip.toggle_sound')} className='equalizer' onClick={onClick}>
        {bars}
      </AccessibleButton>
    </Tooltip>
  );
};

export default Equalizer;
